import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Invoice } from '@shared/models/invoice/invoice.model';
import { OrgDetails } from '@shared/models/org-details.model';
import { User } from '@core/models/user.model';
import { AuthState } from '@core/store/auth/auth.state';
import { InvoiceChatMessage, InvoiceChatMessageReply } from '../../models/invoice-chat-message.model';
import { InvoiceChatService } from '../../services/invoice-chat.service';

interface RenderItem {
  type: 'message' | 'date';
  id?: string;
  message?: InvoiceChatMessage;
  isOwn?: boolean;
  timeLabel?: string;
  dateLabel?: string;
}

@Component({
  selector: 'hmt-invoice-chat-panel',
  templateUrl: './invoice-chat-panel.component.html',
  styleUrls: ['./invoice-chat-panel.component.scss'],
  standalone: false,
})
export class InvoiceChatPanelComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
  @Input() invoice!: Invoice;
  @Input() supplierOrg?: OrgDetails;
  @Input() customerOrg?: OrgDetails;
  @Output() close = new EventEmitter<void>();

  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;

  @Select(AuthState.getUser) user$!: Observable<User>;
  @Select(AuthState.getOrgId) orgId$!: Observable<string>;

  messages: InvoiceChatMessage[] = [];
  renderItems: RenderItem[] = [];
  draft = '';
  loading = true;
  error = false;
  replyingTo: InvoiceChatMessageReply | null = null;

  currentUser: User | null = null;
  currentOrgId: string | null = null;
  currentOrgUserId: string | null = null;

  private destroy$ = new Subject<void>();
  private messagesSub?: Subscription;
  private shouldScrollToBottom = true;
  private lastMessageCount = 0;

  constructor(public readonly chatService: InvoiceChatService) {}

  ngOnInit(): void {
    this.user$
      .pipe(takeUntil(this.destroy$), filter(user => !!user))
      .subscribe(user => {
        this.currentUser = user;
        this.currentOrgUserId = user?.orgUserId || user?.userId || null;
        this.maybeStart();
      });

    this.orgId$
      .pipe(takeUntil(this.destroy$), filter(orgId => !!orgId))
      .subscribe(orgId => {
        this.currentOrgId = orgId;
        this.maybeStart();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoice'] && !changes['invoice'].firstChange) {
      this.restartListener();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.messagesSub?.unsubscribe();
  }

  onClose(): void {
    this.close.emit();
  }

  trackByRenderItem(_: number, item: RenderItem): string {
    return item.type === 'date' ? `d-${item.dateLabel}` : `m-${item.id}`;
  }

  canSend(): boolean {
    return !!this.draft.trim()
      && !!this.currentOrgUserId
      && !!this.currentOrgId
      && this.chatService.canChat(this.invoice, this.currentOrgId);
  }

  send(): void {
    if (!this.canSend() || !this.invoice || !this.currentOrgUserId || !this.currentOrgId) {
      return;
    }
    const text = this.draft;
    const replyTo = this.replyingTo;
    this.draft = '';
    this.replyingTo = null;
    const senderName = this.composeUserName(this.currentUser);
    this.chatService
      .sendMessage(this.invoice, text, this.currentOrgUserId, this.currentOrgId, senderName, replyTo)
      .catch(() => {
        this.error = true;
      });
    this.shouldScrollToBottom = true;
  }

  startReply(message: InvoiceChatMessage | undefined): void {
    if (!message || !message.id) {
      return;
    }
    this.replyingTo = {
      messageId: message.id,
      senderName: message.sentByOrgUserId === this.currentOrgUserId
        ? 'You'
        : (message.sentByName || 'Unknown'),
      text: message.text || '',
    };
  }

  cancelReply(): void {
    this.replyingTo = null;
  }

  scrollToMessage(messageId: string | undefined): void {
    if (!messageId) {
      return;
    }
    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }
    const target = container.querySelector(`[data-msg-id="${messageId}"]`) as HTMLElement | null;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('ic-bubble-flash');
      setTimeout(() => target.classList.remove('ic-bubble-flash'), 1200);
    }
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  headerOrg(): OrgDetails | undefined {
    if (!this.invoice || !this.currentOrgId) {
      return this.supplierOrg || this.customerOrg;
    }
    return this.currentOrgId === this.invoice.serviceProvider
      ? this.customerOrg
      : this.supplierOrg;
  }

  private maybeStart(): void {
    if (!this.invoice || !this.currentOrgUserId || !this.currentOrgId) {
      return;
    }
    if (!this.chatService.canChat(this.invoice, this.currentOrgId)) {
      this.loading = false;
      return;
    }
    if (this.messagesSub) {
      return;
    }
    this.startListener();
  }

  private restartListener(): void {
    this.messagesSub?.unsubscribe();
    this.messagesSub = undefined;
    this.messages = [];
    this.renderItems = [];
    this.lastMessageCount = 0;
    this.maybeStart();
  }

  private startListener(): void {
    if (!this.invoice || !this.currentOrgUserId) {
      return;
    }
    const invoiceId = this.chatService.resolveInvoiceId(this.invoice);
    if (!invoiceId) {
      this.loading = false;
      return;
    }

    this.chatService
      .ensureThread(this.invoice, this.currentOrgUserId)
      .catch(() => undefined);

    this.loading = true;
    this.messagesSub = this.chatService
      .listenToMessages(invoiceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: messages => {
          this.messages = messages;
          this.renderItems = this.buildRenderItems(messages);
          this.loading = false;
          if (messages.length !== this.lastMessageCount) {
            this.shouldScrollToBottom = true;
            this.lastMessageCount = messages.length;
          }
          this.markUnread(invoiceId, messages);
        },
        error: () => {
          this.loading = false;
          this.error = true;
        },
      });
  }

  private markUnread(invoiceId: string, messages: InvoiceChatMessage[]): void {
    if (!this.currentOrgUserId) {
      return;
    }
    const unreadIds = messages
      .filter(m => m.id
        && m.sentByOrgUserId !== this.currentOrgUserId
        && !(m.readBy || []).includes(this.currentOrgUserId!),
      )
      .map(m => m.id!) as string[];
    if (unreadIds.length === 0) {
      return;
    }
    this.chatService
      .markAsRead(invoiceId, unreadIds, this.currentOrgUserId)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe();
  }

  private buildRenderItems(messages: InvoiceChatMessage[]): RenderItem[] {
    const items: RenderItem[] = [];
    let lastDayKey = '';
    for (const message of messages) {
      const created = message.createdAt instanceof Date ? message.createdAt : null;
      const dayKey = created ? this.toDayKey(created) : '';
      if (dayKey && dayKey !== lastDayKey) {
        items.push({ type: 'date', dateLabel: this.toDateLabel(created!) });
        lastDayKey = dayKey;
      }
      items.push({
        type: 'message',
        id: message.id,
        message,
        isOwn: message.sentByOrgUserId === this.currentOrgUserId,
        timeLabel: created ? this.toTimeLabel(created) : '',
      });
    }
    return items;
  }

  private toDayKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  private toDateLabel(date: Date): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[date.getDay()];
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}, ${dd}/${mm}`;
  }

  private toTimeLabel(date: Date): string {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}.${mm}`;
  }

  private scrollToBottom(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }

  private composeUserName(user: User | null): string {
    if (!user) {
      return 'Unknown';
    }
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    const combined = `${first} ${last}`.trim();
    return combined || user.userId || 'Unknown';
  }
}
